/* eslint-disable prettier/prettier */
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Assignee,
  Company,
  isCompanyTest,
  isCustomTest,
  ScheduleTest,
  Topic
} from '../../redux/Slices/ScheduleTestSlice/types';
import { TagType } from '../../redux/Slices/tags';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import { useGetQuestionBankDataQuery } from '../../services/questionBankApi';
import {
  useFetchAssociatedStudentsQuery,
  useGetAllScheduleTestCompaniesQuery,
  useLazyCheckDuplicateTestnameQuery,
  useScheduleTestMutation,
  useScheduleCustomTestMutation
} from '../../services/scheduleTestApi';
import {
  useFetchTagsQuery,
  useLazyFetchTagByIdQuery
} from '../../services/tagsApi';
import { cn } from '../../utils/tailwindMerge';
import Dropdown from '../Common/Dropdown';

import ScheduleTestAssigneesTable from './ScheduleTestAssigneesTable';
import ScheduleTestSections from './ScheduleTestSections';

const ScheduleTestForm = ({ setCurrentScreen }: { setCurrentScreen: any }) => {
  const [activeTab, setActiveTab] = useState<
    'companyTestForm' | 'customizedTestForm'
  >('companyTestForm');
  const [isTestScheduling, setIsTestScheduling] = useState(false);
  const [testData, setTestData] = useState<ScheduleTest>(() => {
    const baseData = {
      organizer: 'Arjun',
      testName: '',
      windowStartDate: '',
      windowEndDate: '',
      description: '',
      instructions: '',
      assignees: []
    };

    if (activeTab === 'companyTestForm') {
      return {
        ...baseData,
        testType: 'company',
        company: { companyName: '', companyId: '' }
      };
    } else {
      return {
        ...baseData,
        testType: 'custom',
        sections: [
          {
            id: `section-${Date.now()}`,
            sectionId: '',
            topics: [],
            questionsCount: 0,
            timeLimit: 0
          }
        ]
      };
    }
  });
  const [topicDropdownOpenIndex, setTopicDropdownOpenIndex] = useState<
    number | null
  >(null);
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] =
    useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<TagType>({} as TagType);
  const [selectedCompany, setSelectedCompany] = useState<Company>(
    {} as Company
  );
  const [showValidationError, setShowValidationError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [dateTimeError, setDateTimeError] = useState('');

  const startTimeRef = useRef<HTMLDivElement>(null);
  const endTimeRef = useRef<HTMLDivElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const userDetails = useAppSelector(state => state.root.auth.studentDetails);
  const { tags } = useAppSelector(state => state.root.tags);
  const dispatch = useAppDispatch();
  const { data: sectionsData } = useGetQuestionBankDataQuery();
  const { data: companiesData } = useGetAllScheduleTestCompaniesQuery({});
  const { data: associatedStudents } = useFetchAssociatedStudentsQuery({});
  const [checkDuplicateTestName] = useLazyCheckDuplicateTestnameQuery();

  useFetchTagsQuery({}, { refetchOnMountOrArgChange: true });
  const [fetchTagById] = useLazyFetchTagByIdQuery();
  const [scheduleTest] = useScheduleTestMutation();
  const [scheduleCustomTest] = useScheduleCustomTestMutation();

  const truncatedName =
    userDetails?.name?.length > 10
      ? `${userDetails?.name.substring(0, 15)}..`
      : userDetails?.name;

  const visibleAssignees = testData.tag?.id
    ? testData.assignees.filter(a => a.isTagMember)
    : testData.assignees;

  // Close time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        startTimeRef.current &&
        !startTimeRef.current.contains(event.target as Node)
      ) {
        setShowStartTimePicker(false);
      }
      if (
        endTimeRef.current &&
        !endTimeRef.current.contains(event.target as Node)
      ) {
        setShowEndTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate time slots with 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 30]) {
        const h = String(hour).padStart(2, '0');
        const m = String(minute).padStart(2, '0');
        slots.push(`${h}:${m}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();
  const now = new Date().toISOString().slice(0, 16);

  const isPastDateTime = (dateTime: string) => {
    return new Date(dateTime).getTime() < new Date().getTime();
  };
  useEffect(() => {
    setTestData(prev => ({
      ...prev,
      assignees: []
    }));
  }, [userDetails?.id]);

  useEffect(() => {
    if (associatedStudents?.data) {
      const converted = associatedStudents?.data.map((s: Assignee) => ({
        ...s,
        selected: false
      }));
      setTestData(prev => ({
        ...prev,
        assignees: converted
      }));
    }
  }, [associatedStudents]);

  useEffect(() => {
    if (!testData?.tag?.id) return;

    fetchTagById(testData.tag.id)
      .unwrap()
      .then(tagRes => {
        const ids: string[] = tagRes.data || [];

        setTestData(prev => ({
          ...prev,
          assignees: prev.assignees.map(s => ({
            ...s,
            isTagMember: ids.includes(s.studentId),
            selected: ids.includes(s.studentId)
          }))
        }));
      });
  }, [testData.tag?.id]);

  useEffect(() => {
    const newTestType = activeTab === 'companyTestForm' ? 'company' : 'custom';
    setTestData(prev => {
      const baseData = {
        organizer: prev.organizer,
        testName: '',
        windowStartDate: '',
        windowEndDate: '',
        description: '',
        instructions: '',
        assignees: prev.assignees.map(a => ({ ...a, selected: false })),
        tag: prev.tag
      };

      if (newTestType === 'company') {
        return {
          ...baseData,
          testType: 'company',
          company: { companyName: '', companyId: '' }
        };
      } else {
        return {
          ...baseData,
          testType: 'custom',
          sections: [
            {
              id: `section-${Date.now() + Math.random()}`,
              sectionId: '',
              topics: [],
              questionsCount: 0,
              timeLimit: 0
            }
          ]
        };
      }
    });

    setShowValidationError(false);
    setDateTimeError('');
    setTopicDropdownOpenIndex(null);
    setIsTopicDropdownOpen(false);
    setShowStartTimePicker(false);
    setShowEndTimePicker(false);
  }, [activeTab]);

  // Get available sections
  const getAvailableSections = (index: number) => {
    const allSections = sectionsData?.data?.sections || [];

    if (isCustomTest(testData)) {
      //Get already section Ids
      const selectedSectionIds = testData.sections
        ?.filter((_, idx) => idx !== index)
        ?.map(section => section.sectionId)
        ?.filter(Boolean);

      return allSections.filter(
        section => !selectedSectionIds.includes(section.sectionId)
      );
    }
    return allSections;
  };

  // Get topics for selected section
  const getAvailableTopics = (sectionId: string): Topic[] => {
    return (
      sectionsData?.data?.sections?.find(sec => sec.sectionId === sectionId)
        ?.topics || []
    );
  };

  // Add a new section
  const handleAddSection = () => {
    if (isCustomTest(testData) && testData.sections?.length < 4) {
      setTestData(prev => ({
        ...prev,
        sections: [
          ...testData.sections,
          {
            id: `section-${Date.now() + Math.random()}`,
            sectionId: '',
            topics: [],
            questionsCount: 0,
            timeLimit: 0
          }
        ]
      }));
    }
  };

  // Remove a section
  const handleRemoveSection = (index: number) => {
    if (!isCustomTest(testData)) return;

    if (testData.sections?.length === 1) {
      toast.warning('At least one section is required');
      return;
    }

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      return {
        ...prev,
        sections: prev.sections?.filter((_, idx) => idx !== index)
      };
    });
  };

  //Handle section change for a specific section
  const handleSectionChange = (sectionId: string, index: number): void => {
    if (!isCustomTest(testData)) return;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      return {
        ...prev,
        sections: prev.sections?.map((section, idx) =>
          idx === index
            ? {
                ...section,
                sectionId,
                topics: [],
                questionsCount: 1,
                timeLimit: 1
              }
            : section
        )
      };
    });

    setTopicDropdownOpenIndex(null);
  };

  // Handle topic toggle for a specific section
  const handleTopicToggle = async (topicId: string, index: number) => {
    if (!isCustomTest(testData)) return;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const updatedSections = prev.sections?.map((section, idx) => {
        if (idx === index) {
          const currentTopics = section?.topics;
          const updatedTopics = currentTopics.includes(topicId)
            ? currentTopics.filter(id => id !== topicId)
            : [...currentTopics, topicId];

          return { ...section, topics: updatedTopics };
        }
        return section;
      });

      return { ...prev, sections: updatedSections };
    });

    setErrorMessage('');
  };

  // Remove topic from specific section
  const removeTopic = async (topicId: string, index: number) => {
    if (!isCustomTest(testData)) return;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const updatedSections = prev.sections?.map((section, idx) =>
        idx === index
          ? { ...section, topics: section.topics.filter(id => id !== topicId) }
          : section
      );

      return { ...prev, sections: updatedSections };
    });
  };

  // Handle questions count change for a specific section
  const handleQuestionsCountChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (!isCustomTest(testData)) return;

    const inputValue = e.target.value;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const updatedSections = prev.sections?.map((section, idx) => {
        if (idx === index) {
          if (inputValue === '') {
            return { ...section, questionsCount: 0 };
          } else {
            const value = Number(inputValue);
            const maxAllowed = 30;
            let finalValue = value;

            if (isNaN(value) || value < 1) {
              finalValue = 0;
            } else if (value > maxAllowed) {
              finalValue = maxAllowed;
            } else {
              finalValue = value;
            }
            return { ...section, questionsCount: finalValue };
          }
        }
        return section;
      });

      return { ...prev, sections: updatedSections };
    });
  };

  // Handle time limit change for a specific section
  const handleTimeLimitChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    if (!isCustomTest(testData)) return;

    const inputValue = e.target.value;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const updatedSections = prev.sections?.map((section, idx) => {
        if (idx === index) {
          if (inputValue === '') {
            return { ...section, timeLimit: 0 };
          } else {
            const minutes = Number(inputValue);
            const maxAllowed = 180;
            let finalMinutes = minutes;

            if (isNaN(minutes) || minutes < 1) {
              finalMinutes = 0;
            } else if (minutes > maxAllowed) {
              finalMinutes = maxAllowed;
            } else {
              finalMinutes = minutes;
            }
            return { ...section, timeLimit: finalMinutes };
          }
        }
        return section;
      });

      return { ...prev, sections: updatedSections };
    });
  };

  const handleQuestionsCountArrow = async (
    index: number,
    direction: 'up' | 'down'
  ) => {
    if (!isCustomTest(testData)) return;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const current = prev.sections?.[index]?.questionsCount ?? 0;
      let newValue = current;

      if (direction === 'up') {
        if (current < 30) {
          newValue = current === 0 ? 1 : current + 1;
        } else {
          return prev;
        }
      } else {
        if (current > 0) {
          newValue = current - 1;
          if (newValue === 0) newValue = 0;
        } else {
          return prev;
        }
      }

      const updatedSections = prev.sections?.map((section, idx) =>
        idx === index ? { ...section, questionsCount: newValue } : section
      );

      return { ...prev, sections: updatedSections };
    });
  };

  const handleTimeLimitArrow = (
    index: number,
    direction: 'up' | 'down'
  ): void => {
    if (!isCustomTest(testData)) return;

    setTestData(prev => {
      if (!isCustomTest(prev)) return prev;

      const current = prev.sections?.[index]?.timeLimit ?? 0;
      let newValue = current;

      if (direction === 'up') {
        if (current < 180) {
          newValue = current === 0 ? 1 : current + 1;
        } else {
          return prev;
        }
      } else {
        if (current > 0) {
          newValue = current - 1;
          if (newValue === 0) newValue = 0;
        } else {
          return prev;
        }
      }

      const updatedSections = prev.sections?.map((section, idx) =>
        idx === index ? { ...section, timeLimit: newValue } : section
      );

      return { ...prev, sections: updatedSections };
    });
  };

  // Get selected topic names for a specific section
  const getSelectedTopicNames = (index: number): string[] => {
    if (!isCustomTest(testData)) return [];

    const section = testData.sections?.[index];
    const availableTopics = getAvailableTopics(section.sectionId);

    return section.topics
      ?.map(
        topicId => availableTopics.find(t => t.topicId === topicId)?.topicName
      )
      ?.filter((name): name is string => Boolean(name));
  };

  // Get topic text color
  const getTopicTextColor = (index: number) => {
    if (!isCustomTest(testData)) return 'text-gray-400';

    return testData.sections?.[index]?.topics.length === 0
      ? 'text-gray-400'
      : 'text-gray-900';
  };

  // Get topic text for display
  const getTopicText = (index: number): string => {
    if (!isCustomTest(testData)) return 'text-gray-400';

    const count = testData.sections?.[index]?.topics?.length;

    if (count === 0) {
      return 'Selected topics';
    }

    const plural = count > 1 ? 's' : '';
    return `${count} topics${plural} selected`;
  };

  const selectedStudents = useMemo(() => {
    return testData.assignees?.filter(a => a.selected);
  }, [testData.assignees]);

  useEffect(() => {
    setShowValidationError(false);
  }, [
    testData?.testName,
    testData?.windowStartDate,
    testData?.windowEndDate,
    isCompanyTest(testData) ? testData.company?.companyId : '',
    isCustomTest(testData) ? testData.sections : [],
    selectedStudents,
    activeTab
  ]);

  const handleToggleAssignee = (studentId: string) => {
    setTestData(prev => ({
      ...prev,
      assignees: prev.assignees.map(a =>
        a.studentId === studentId ? { ...a, selected: !a.selected } : a
      )
    }));
  };

  // Select All checkbox
  const handleSelectAll = () => {
    const isTagSelected = Boolean(testData.tag?.id);

    setTestData(prev => {
      const targetAssignees = isTagSelected
        ? prev.assignees.filter(a => a.isTagMember)
        : prev.assignees;

      const allSelected =
        targetAssignees.length > 0 && targetAssignees.every(a => a.selected);

      return {
        ...prev,
        assignees: prev.assignees.map(a => {
          // 🚫 do not touch non-tag students
          if (isTagSelected && !a.isTagMember) {
            return a;
          }
          return { ...a, selected: !allSelected };
        })
      };
    });
  };

  function toISOZFormat(dateStr: string): string {
    return new Date(dateStr).toISOString();
  }

  const checkDuplicateTestNames = async (testName: string) => {
    try {
      await checkDuplicateTestName(testName).unwrap();
      return true;
    } catch (error: any) {
      if (error?.status === 409) {
        toast.error('Test Name Already Exists');
      }
      return false;
    }
  };

  const disableCreateTestBtn = () => {
    const { testName, windowEndDate, windowStartDate } = testData;

    if (
      !testName.trim() ||
      !windowStartDate ||
      !windowEndDate ||
      dateTimeError
    ) {
      return true;
    }

    if (selectedStudents?.length === 0) {
      return true;
    }
    if (isCompanyTest(testData)) {
      return !testData.company?.companyId;
    } else {
      return !testData.sections.every(
        section =>
          section?.sectionId &&
          section?.topics.length > 0 &&
          section?.questionsCount > 0 &&
          section?.timeLimit > 0
      );
    }
  };

  const handleCreateTest = async () => {
    const trimmedTestName = testData.testName.trim();

    if (!trimmedTestName) {
      toast.error('Please enter a valid Test Name');
      setShowValidationError(true);
      return;
    }

    if (trimmedTestName.length > 20) {
      toast.error('Test Name must be at most 20 characters');
      return;
    }

    if (disableCreateTestBtn()) {
      setShowValidationError(true);
      return;
    }

    setIsTestScheduling(true);

    try {
      const isValidTestName = await checkDuplicateTestNames(testData.testName);
      if (!isValidTestName) return;

      const commonBody = {
        testStartTime: toISOZFormat(testData.windowStartDate),
        testEndTime: toISOZFormat(testData.windowEndDate),
        organizerId: userDetails?.id || '',
        tenantId: userDetails?.id || '',
        tagId: testData.tag?.id || '',
        testName: trimmedTestName,
        assignedStudentIds: selectedStudents.map(s => s.studentId)
      };

      if (isCompanyTest(testData)) {
        await scheduleTest({
          ...commonBody,
          companyId: testData.company.companyId
        }).unwrap();
      } else {
        await scheduleCustomTest({
          ...commonBody,
          sectionDetails: testData.sections.map(section => ({
            sectionId: section.sectionId,
            topicIds: section.topics,
            numberOfQuestions: section.questionsCount,
            timeAllotted: section.timeLimit * 60
          }))
        }).unwrap();
      }

      toast.success('Test scheduled successfully');

      // reset form
      setTestData(prev => ({
        ...prev,
        testName: '',
        windowStartDate: '',
        windowEndDate: '',
        assignees: prev.assignees.map(a => ({ ...a, selected: false })),
        ...(isCompanyTest(prev)
          ? { company: { companyName: '', companyId: '' } }
          : {
              sections: [
                {
                  id: `section-${Date.now()}`,
                  sectionId: '',
                  topics: [],
                  questionsCount: 0,
                  timeLimit: 0
                }
              ]
            })
      }));
    } catch (error: any) {
      const apiMessage =
        error?.data?.message ||
        error?.error ||
        'Not enough questions available for the selected topics';

      toast.error(apiMessage);
    } finally {
      setIsTestScheduling(false);
      setShowValidationError(false);
    }
  };

  return (
    <div className='flex w-full flex-col justify-center bg-gray-50 px-8 pb-4 pt-3'>
      <div className='mx-auto w-full max-w-5xl'>
        <div className='mb-8 flex gap-3'>
          <button
            onClick={() => {
              if (activeTab === 'customizedTestForm') {
                setActiveTab('companyTestForm');
              } else {
                setCurrentScreen('dashboard');
              }
            }}
            className='mb-1 rounded-xl py-1 font-medium text-primary transition-colors hover:bg-gray-50'
          >
            <ArrowLeft />
          </button>
          <button
            onClick={() => setActiveTab('companyTestForm')}
            className={`flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold shadow-sm ${
              activeTab === 'companyTestForm'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Schedule Company Tests
          </button>
          <button
            onClick={() => setActiveTab('customizedTestForm')}
            className={`flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold shadow-sm ${
              activeTab === 'customizedTestForm'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Schedule Customized Test
          </button>
        </div>
        <div className='w-full rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'>
          <div className='space-y-6'>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              <div className='flex flex-col'>
                <label className='mb-2 block text-sm font-semibold text-gray-900'>
                  Organizer <span className='text-[red]'>*</span>
                </label>

                <input
                  readOnly
                  type='text'
                  value={truncatedName}
                  className='w-full rounded-xl border border-gray-300 px-4 py-3 text-blue-600 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                />
              </div>
              <div className='flex flex-col'>
                <Dropdown
                  getOption={(option: any) => `${option?.tagName}`}
                  options={tags ?? []}
                  label='Batch Name'
                  value={testData.tag?.tagName ?? ''}
                  handleDropdownChange={e => {
                    setTestData(prev => ({
                      ...prev,
                      tag: e
                    }));
                  }}
                  width='w-[100%]'
                  placeholder='Select Batch Name'
                  classes='h-[50px] rounded-xl border border-gray-300 w-full'
                />{' '}
              </div>
            </div>

            {isCompanyTest(testData) && (
              <div className='grid w-full grid-cols-1 gap-4 lg:grid-cols-2'>
                <div className='w-full'>
                  <Dropdown
                    getOption={(option: any) => `${option?.companyName}`}
                    options={companiesData?.data ?? []}
                    label='Company Name'
                    value={testData.company?.companyName ?? ''}
                    handleDropdownChange={e => {
                      setTestData(prev => ({
                        ...prev,
                        company: {
                          companyName: e.companyName,
                          companyId: e.companyId
                        }
                      }));
                    }}
                    width='w-[100%]'
                    placeholder='Select Company Name'
                    classes='h-[50px] rounded-xl border border-gray-300'
                    isRequired
                  />{' '}
                </div>
                <div>
                  <label className='mb-2 block text-sm font-semibold text-gray-900'>
                    Test Name <span className='text-[red]'>*</span>
                  </label>
                  <input
                    type='text'
                    placeholder='Enter Test Name'
                    value={testData.testName}
                    maxLength={25}
                    onChange={e => {
                      const value = e.target.value;
                      if (
                        value.length === 25 &&
                        testData.testName.length === 24
                      ) {
                        toast.error('Test Name must be at most 25 characters');
                      }

                      setTestData({
                        ...testData,
                        testName: value
                      });
                    }}
                    onPaste={e => {
                      const pastedText = e.clipboardData.getData('text');
                      if (pastedText.length > 2) {
                        toast.error('Test Name must be at most 25 characters');
                      }
                    }}
                    className='w-full rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                  />
                </div>
              </div>
            )}
            {isCustomTest(testData) && (
              <>
                <div className='grid w-full grid-cols-1 gap-4 lg:grid-cols-1'>
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-gray-900'>
                      Test Name <span className='text-[red]'>*</span>
                    </label>
                    <input
                      type='text'
                      placeholder='Enter Test Name'
                      value={testData.testName}
                      onChange={e =>
                        setTestData({ ...testData, testName: e.target.value })
                      }
                      className='w-full rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                  </div>
                </div>

                <ScheduleTestSections
                  sections={testData.sections}
                  onAddSection={handleAddSection}
                  onRemoveSection={handleRemoveSection}
                  onSectionChange={handleSectionChange}
                  onTopicToggle={handleTopicToggle}
                  onRemoveTopic={removeTopic}
                  onQuestionsCountChange={handleQuestionsCountChange}
                  onTimeLimitChange={handleTimeLimitChange}
                  onQuestionsCountArrow={direction => index =>
                    handleQuestionsCountArrow(index, direction)
                  }
                  onTimeLimitArrow={direction => index =>
                    handleTimeLimitArrow(index, direction)
                  }
                  topicDropdownOpenIndex={topicDropdownOpenIndex}
                  setTopicDropdownOpenIndex={setTopicDropdownOpenIndex}
                  isTopicDropdownOpen={isTopicDropdownOpen}
                  setIsTopicDropdownOpen={setIsTopicDropdownOpen}
                  getAvailableSections={getAvailableSections}
                  getAvailableTopics={getAvailableTopics}
                  getSelectedTopicNames={getSelectedTopicNames}
                  getTopicTextColor={getTopicTextColor}
                  getTopicText={getTopicText}
                />
              </>
            )}
            <div className='grid grid-cols-2 gap-4'>
              {/* Test Start Date */}
              <div>
                <label className='mb-2 block text-sm font-semibold text-gray-900'>
                  Test Start Date <span className='text-[red]'>*</span>
                </label>

                <div className='flex gap-2'>
                  {/* Date Picker */}
                  <div
                    className='relative flex-1'
                    onClick={() => startDateRef.current?.showPicker()}
                  >
                    <input
                      ref={startDateRef}
                      type='date'
                      min={now.split('T')[0]}
                      value={
                        testData.windowStartDate
                          ? testData.windowStartDate.split('T')[0]
                          : ''
                      }
                      onChange={e => {
                        const time = testData.windowStartDate
                          ? testData.windowStartDate.split('T')[1]
                          : '00:00';
                        const newStartDateTime = e.target.value
                          ? `${e.target.value}T${time}`
                          : '';

                        setTestData({
                          ...testData,
                          windowStartDate: newStartDateTime
                        });

                        // Clear error when changing start date
                        setDateTimeError('');
                      }}
                      className='w-full cursor-pointer rounded-xl border border-gray-300 px-4 py-3 pr-12 text-black focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                      style={{
                        colorScheme: 'light'
                      }}
                    />
                    <style>{`
                      input[type='date']::-webkit-calendar-picker-indicator {
                        opacity: 0;
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        cursor: pointer;
                      }
                    `}</style>
                    <Calendar className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-blue-600' />
                  </div>

                  {/* Time Picker */}
                  <div className='relative flex-1' ref={startTimeRef}>
                    <button
                      type='button'
                      onClick={() =>
                        setShowStartTimePicker(!showStartTimePicker)
                      }
                      className='flex w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-4 py-3 text-left focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                    >
                      <span
                        className={
                          testData.windowStartDate?.split('T')[1]
                            ? 'text-black'
                            : 'text-gray-400'
                        }
                      >
                        {testData.windowStartDate?.split('T')[1] || 'HH:MM'}
                      </span>
                      <Clock className='h-5 w-5 text-blue-600' />
                    </button>

                    {showStartTimePicker && (
                      <div className='absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                        {timeSlots.map(time => (
                          <button
                            key={time}
                            type='button'
                            onClick={() => {
                              const date =
                                testData.windowStartDate?.split('T')[0] ||
                                new Date().toISOString().split('T')[0];
                              const newStartDateTime = `${date}T${time}`;

                              if (isPastDateTime(newStartDateTime)) {
                                setDateTimeError(
                                  'Start date and time cannot be in the past'
                                );
                                return;
                              }

                              setDateTimeError('');
                              setTestData({
                                ...testData,
                                windowStartDate: newStartDateTime
                              });
                              setShowStartTimePicker(false);
                            }}
                            className='w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-100'
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Test End Date */}
              <div>
                <label className='mb-2 block text-sm font-semibold text-gray-900'>
                  Test End Date <span className='text-[red]'>*</span>
                </label>

                <div className='flex gap-2'>
                  {/* Date Picker */}
                  <div
                    className='relative flex-1'
                    onClick={() => endDateRef.current?.showPicker()}
                  >
                    <input
                      ref={endDateRef}
                      type='date'
                      min={
                        testData.windowStartDate
                          ? testData.windowStartDate.split('T')[0]
                          : now.split('T')[0]
                      }
                      value={
                        testData.windowEndDate
                          ? testData.windowEndDate.split('T')[0]
                          : ''
                      }
                      onChange={e => {
                        const time = testData.windowEndDate
                          ? testData.windowEndDate.split('T')[1]
                          : '00:00';
                        const newEndDateTime = e.target.value
                          ? `${e.target.value}T${time}`
                          : '';

                        // Validate if end date/time is after start date/time
                        if (
                          testData.windowStartDate &&
                          new Date(newEndDateTime).getTime() <=
                            new Date(testData.windowStartDate).getTime()
                        ) {
                          setDateTimeError(
                            'End date and time must be after start date and time'
                          );
                        } else {
                          setDateTimeError('');
                        }

                        setTestData({
                          ...testData,
                          windowEndDate: newEndDateTime
                        });
                      }}
                      className='w-full cursor-pointer rounded-xl border border-gray-300 px-4 py-3 pr-12 text-black focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                      style={{
                        colorScheme: 'light'
                      }}
                    />
                    <style>{`
                      input[type='date']::-webkit-calendar-picker-indicator {
                        opacity: 0;
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        cursor: pointer;
                      }
                    `}</style>
                    <Calendar className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-blue-600' />
                  </div>

                  {/* Time Picker */}
                  <div className='relative flex-1' ref={endTimeRef}>
                    <button
                      type='button'
                      onClick={() => setShowEndTimePicker(!showEndTimePicker)}
                      className='flex w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-4 py-3 text-left focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                    >
                      <span
                        className={
                          testData.windowEndDate?.split('T')[1]
                            ? 'text-black'
                            : 'text-gray-400'
                        }
                      >
                        {testData.windowEndDate?.split('T')[1] || 'HH:MM'}
                      </span>
                      <Clock className='h-5 w-5 text-blue-600' />
                    </button>

                    {showEndTimePicker && (
                      <div className='absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                        {timeSlots.map(time => (
                          <button
                            key={time}
                            type='button'
                            onClick={() => {
                              const date =
                                testData.windowEndDate?.split('T')[0] ||
                                new Date().toISOString().split('T')[0];
                              const newEndDateTime = `${date}T${time}`;

                              if (isPastDateTime(newEndDateTime)) {
                                setDateTimeError(
                                  'End date and time cannot be in the past'
                                );
                                return;
                              }

                              if (
                                testData.windowStartDate &&
                                new Date(newEndDateTime).getTime() <=
                                  new Date(testData.windowStartDate).getTime()
                              ) {
                                setDateTimeError(
                                  'End date and time must be after start date and time'
                                );
                                return;
                              }

                              setDateTimeError('');
                              setTestData({
                                ...testData,
                                windowEndDate: newEndDateTime
                              });

                              setShowEndTimePicker(false);
                            }}
                            className='w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-100'
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {dateTimeError && (
              <div className='col-span-2 mt-0 text-sm text-red-600'>
                {dateTimeError}
              </div>
            )}

            {/* <div>
                <label className='mb-2 block text-sm font-medium text-gray-900'>
                  Test Description
                </label>
                <textarea
                  placeholder='Add Test Description'
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                  className='w-full resize-none rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                />
              </div>

              <div>
                <label className='mb-2 block text-sm font-medium text-gray-900'>
                  Test Instructions
                </label>
                <textarea
                  placeholder='Add Test Instructions'
                  value={formData.instructions}
                  onChange={e =>
                    setFormData({ ...formData, instructions: e.target.value })
                  }
                  rows={4}
                  className='w-full resize-none rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                />
              </div> */}
            {/* Assignees */}
            <div className='w-full pt-4'>
              <div className='w-full table-auto'>
                <div className='mx-auto w-full max-w-7xl'>
                  {visibleAssignees.length > 0 && (
                    <ScheduleTestAssigneesTable
                      assignees={visibleAssignees}
                      selectedTag={testData?.tag}
                      onToggleAssignee={handleToggleAssignee}
                      onSelectAll={handleSelectAll}
                    />
                  )}
                  {/*validation error message*/}
                  {showValidationError && (
                    <div className='mt-6 text-center'>
                      <p className='text-sm font-medium text-red-600'>
                        <span className='text-red-500'>*</span> Please enter all
                        the required fields to create a test
                      </p>
                    </div>
                  )}
                  <div className='mt-6 flex justify-center gap-4'>
                    <button
                      disabled={isTestScheduling || dateTimeError !== ''}
                      onClick={handleCreateTest}
                      className={cn(
                        'rounded-xl px-6 py-3 font-medium text-white transition-colors',
                        isTestScheduling || dateTimeError !== ''
                          ? 'cursor-not-allowed bg-blue-600 opacity-40'
                          : 'bg-blue-600 hover:bg-blue-700'
                      )}
                    >
                      Create Test
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTestForm;
